// src/components/SmartLists/SmartListsSheetRow.jsx
import React from 'react';
import { Box, Tooltip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import SmartListsGridCell from './SmartListsGridCell';
import {
  ROW_NUMBER_WIDTH,
  GRID_ROW_HEIGHT,
  COLUMN_CONFIG,
} from './smartListsGridConfig';

/**
 * SmartListsSheetRow
 * -----------------------------------------------------------------------------
 * One unified spreadsheet row for the SmartLists workspace.
 *
 * WHY THIS FILE MATTERS
 *  • This is the first real step toward a Google-Sheets-like layout
 *  • Instead of rendering row numbers separately from the sheet body,
 *    this component renders:
 *      - the row number cell
 *      - every grouped SmartList field cell for the same row index
 *    inside one row container
 *
 * BENEFITS
 *  • Row numbers stay aligned automatically with the visible row
 *  • Future row-height changes can be controlled at the row level
 *  • Validation indicators can remain attached to the row they belong to
 *  • This gives us a scalable foundation for:
 *      - row resizing
 *      - sticky row numbers
 *      - multi-group row rendering
 *      - future range selection / copy-paste behavior
 *
 * CURRENT SCOPE
 *  • Renders one row across all grouped SmartList fields
 *  • Uses SmartListsGridCell for each editable cell
 *  • Shows row-level validation indicators when needed
 *  • Supports blank rows correctly
 *
 * IMPORTANT
 *  • This component is row-driven, not group-driven
 *  • It expects each group passed in to already contain `renderedRows`
 *  • It does NOT own keyboard navigation or selection logic
 *  • It does NOT own column resize logic
 * -----------------------------------------------------------------------------
 */

const SmartListsSheetRow = ({
  rowIndex,
  groups = [],

  selectedCell,
  registerCellRef,
  onSelectCell,
  onChangeValue,
  onKeyDown,
  onPaste,

  columnWidths = {},
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        minHeight: `${GRID_ROW_HEIGHT}px`,
        height: `${GRID_ROW_HEIGHT}px`,
        boxSizing: 'border-box',
      }}
    >
      {/* ---------------------------------------------------------------------
       * Sticky row number cell
       * -------------------------------------------------------------------
       * Google-Sheets-like behavior:
       *  • remains visible while horizontally scrolling
       *  • stays visually separated from the scrolling grid
       *  • uses a slightly elevated z-index so body cells never paint over it
       * ------------------------------------------------------------------- */}
      <Box
        sx={{
          position: 'sticky',
          left: 0,
          zIndex: 5,
          minWidth: `${ROW_NUMBER_WIDTH}px`,
          width: `${ROW_NUMBER_WIDTH}px`,
          flexShrink: 0,
          height: `${GRID_ROW_HEIGHT}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: '1px solid #dadce0',
          borderBottom: '1px solid #e8eaed',
          backgroundColor: '#f8f9fa',
          fontSize: '11px',
          boxSizing: 'border-box',

          // subtle visual separation from horizontally scrolling cells
          boxShadow: '1px 0 0 #dadce0',
        }}
      >
        {rowIndex + 1}
      </Box>

      {/* ---------------------------------------------------------------------
       * All SmartList grouped cells for this one row
       * ------------------------------------------------------------------- */}
      {groups.map((group) => {
        const groupId = group?.localDraftId;
        if (!groupId) return null;

        const row = Array.isArray(group?.renderedRows)
          ? group.renderedRows[rowIndex] || null
          : null;

        const isBlankRow = row?.isBlankRow === true;

        const rowHasValidationRisk =
          !isBlankRow &&
          row?.liveFrom &&
          row?.liveTo &&
          row.liveTo < row.liveFrom;

        return (
          <Box
            key={`${groupId}-sheet-row-${rowIndex}`}
            sx={{
              display: 'flex',
              position: 'relative',
            }}
          >
            {COLUMN_CONFIG.map((baseColumn) => {
              const column = {
                ...baseColumn,
                width: columnWidths?.[baseColumn.key] || baseColumn.width,
              };

              const isSelected =
                selectedCell?.groupId === groupId &&
                selectedCell?.rowIndex === rowIndex &&
                selectedCell?.colKey === column.key;

              return (
                <SmartListsGridCell
                  key={`${groupId}-${rowIndex}-${column.key}`}
                  groupId={groupId}
                  rowIndex={rowIndex}
                  column={column}
                  row={row || {}}
                  isSelected={isSelected}
                  registerCellRef={registerCellRef}
                  onSelectCell={onSelectCell}
                  onChangeValue={onChangeValue}
                  onKeyDown={onKeyDown}
                  onPaste={onPaste}
                />
              );
            })}

            {/* ---------------------------------------------------------------
             * Row-level validation indicator
             * ------------------------------------------------------------- */}
            {rowHasValidationRisk && (
              <Tooltip title="End Date is earlier than Launch Date." arrow>
                <Box
                  sx={{
                    position: 'absolute',
                    right: 4,
                    top: 8,
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <ErrorOutlineIcon sx={{ fontSize: '14px', color: '#d32f2f' }} />
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default SmartListsSheetRow;